import { useApolloClient, useSuspenseQuery } from '@apollo/client';
import { useMemo } from 'react';

import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';

import {
  EntityPageCoverImageDocument,
  type EntityPageCoverImageQuery,
} from '../../graphql/queries/cover-image-query.gql-types';

import { NORMAL_DIMENSIONS } from './constants';

export type CoverImages = Extract<
  NonNullable<EntityPageCoverImageQuery['entityPageEX']>,
  { __typename?: 'EntityPage' }
>['coverImage'];

type Response =
  | {
      error: Error;
      coverImages: undefined;
      updateCoverCache: (coverUrl?: string) => void;
    }
  | {
      error: undefined;
      coverImages: CoverImages;
      updateCoverCache: (coverUrl?: string) => void;
    };

export const useCoverMedia = (): Response => {
  const apolloClient = useApolloClient();
  const { pageContext } = usePageContext();

  const variables = { id: pageContext.pageSlug, dimensions: NORMAL_DIMENSIONS };
  const { data, error } = useSuspenseQuery(EntityPageCoverImageDocument, {
    variables,
    errorPolicy: 'all',
  });

  const updateCoverCache = (coverUrl?: string) => {
    const response = apolloClient.readQuery({
      query: EntityPageCoverImageDocument,
      variables,
    });

    const entityPage = response?.entityPageEX;

    if (entityPage?.__typename !== 'EntityPage') {
      return;
    }

    if (!coverUrl) {
      apolloClient.writeQuery({
        query: EntityPageCoverImageDocument,
        variables,
        data: {
          ...response,
          entityPageEX: {
            ...entityPage,
            coverImage: [],
          },
        },
      });
    }

    apolloClient.writeQuery({
      query: EntityPageCoverImageDocument,
      variables,
      overwrite: true,
      data: {
        ...response,
        entityPageEX: {
          ...entityPage,
          coverImage: entityPage.coverImage?.map((image) => ({
            ...image,
            url: coverUrl,
          })),
        },
      },
    });
  };

  return useMemo(() => {
    if (error) {
      return { error, coverImages: undefined, updateCoverCache };
    }

    const entityPage = data?.entityPageEX;

    if (entityPage?.__typename !== 'EntityPage') {
      return {
        error: Error('EntityPage Error'),
        coverImages: undefined,
        updateCoverCache,
      };
    }

    const coverImages = entityPage.coverImage;

    return { error: undefined, coverImages, updateCoverCache };
  }, [data, error, updateCoverCache]);
};
